'use client';

import React from 'react';
import {
  DEFAULT_STICKY_NAV_OFFSET_PX,
  isElementDisplayed,
  measureStickyNavOffset,
  setStickyNavOffset,
  clearStickyNavOffset,
  useResizeObserver,
} from '@benzinga/hooks';

/**
 * Measures a navigation header node and writes `--bz-sticky-nav-offset`.
 * `initialOffsetPx` should match the SSR placeholder estimate to avoid first-paint CLS.
 */
export function useSyncStickyNavOffset(
  headerRef: React.RefObject<HTMLElement | null>,
  isSticky: boolean,
  initialOffsetPx = DEFAULT_STICKY_NAV_OFFSET_PX,
): number {
  const [stickyNavHeight, setStickyNavHeight] = React.useState(initialOffsetPx);

  React.useEffect(() => {
    setStickyNavHeight(previousHeight =>
      Math.abs(previousHeight - initialOffsetPx) <= 1 ? previousHeight : initialOffsetPx,
    );
  }, [initialOffsetPx]);

  const syncStickyNavOffset = React.useCallback(() => {
    if (!isSticky) return;
    const headerElement = headerRef.current;
    if (!headerElement || headerElement.getClientRects().length === 0 || !isElementDisplayed(headerElement)) {
      return;
    }
    const measuredOffsetPx = measureStickyNavOffset(headerElement);
    if (measuredOffsetPx <= 0) return;
    setStickyNavHeight(previousHeight =>
      Math.abs(previousHeight - measuredOffsetPx) <= 1 ? previousHeight : measuredOffsetPx,
    );
    setStickyNavOffset(measuredOffsetPx);
  }, [isSticky, headerRef]);

  useResizeObserver(headerRef, syncStickyNavOffset, { enabled: isSticky });

  React.useLayoutEffect(() => {
    if (!isSticky) {
      clearStickyNavOffset();
      return;
    }
    setStickyNavOffset(initialOffsetPx);
    syncStickyNavOffset();
  }, [isSticky, initialOffsetPx, syncStickyNavOffset]);

  return stickyNavHeight;
}
